import { Encryption } from './encryption';
import { dynamicConfig } from '../config/dynamicConfig';
import { defaultDemoConfig } from '../config/demoConfig';
const STORAGE_KEY = 'demo-config';
const API_KEYS_STORAGE_KEY = 'demo-api-keys';
export class ConfigManager {
    static saveConfig(config) {
        try {
            // Save main config without API keys
            const configWithoutKeys = Object.assign(Object.assign({}, config), { search: Object.assign(Object.assign({}, config.search), { apiKeys: {} }) });
            localStorage.setItem(STORAGE_KEY, JSON.stringify(configWithoutKeys));
            // Save API keys separately with encryption
            if (config.search.apiKeys) {
                const encryptedKeys = Encryption.encryptApiKeys(config.search.apiKeys);
                localStorage.setItem(API_KEYS_STORAGE_KEY, JSON.stringify(encryptedKeys));
            }
        }
        catch (error) {
            console.error('Failed to save config:', error);
        }
    }
    static loadConfig() {
        try {
            const savedConfig = localStorage.getItem(STORAGE_KEY);
            if (!savedConfig)
                return null;
            const config = JSON.parse(savedConfig);
            // Load and decrypt API keys
            const savedApiKeys = localStorage.getItem(API_KEYS_STORAGE_KEY);
            if (savedApiKeys) {
                const encryptedKeys = JSON.parse(savedApiKeys);
                config.search.apiKeys = Encryption.decryptApiKeys(encryptedKeys);
            }
            return config;
        }
        catch (error) {
            console.error('Failed to load config:', error);
            return null;
        }
    }
    /**
     * Load configuration with environment variable priority
     * Priority: Environment variables > Control Panel (localStorage) > Defaults
     */
    static loadConfigWithEnvPriority() {
        // Get the dynamic config that includes env vars
        const envConfig = dynamicConfig.getConfig();
        // Get saved config from localStorage
        const savedConfig = this.loadConfig();
        // If env provider is set and different from saved, use env provider
        const provider = envConfig.provider !== 'mock' || !savedConfig ?
            envConfig.provider : savedConfig.search.provider;
        // Merge configs with proper priority
        if (savedConfig) {
            // Update saved config with env provider if set
            if (process.env.PROVIDER) {
                savedConfig.search.provider = provider;
                // Also update API keys from env if provider matches
                if (provider === 'unbxd' && envConfig.unbxd.apiKey) {
                    savedConfig.search.apiKeys.unbxd = {
                        apiKey: envConfig.unbxd.apiKey,
                        siteKey: envConfig.unbxd.siteKey
                    };
                }
                else if (provider === 'algolia' && envConfig.algolia.appId) {
                    savedConfig.search.apiKeys.algolia = {
                        appId: envConfig.algolia.appId,
                        apiKey: envConfig.algolia.apiKey,
                        indexName: envConfig.algolia.indexName
                    };
                }
                else if (provider === 'shopify' && envConfig.shopify.domain) {
                    savedConfig.search.apiKeys.shopify = {
                        domain: envConfig.shopify.domain,
                        storefrontToken: envConfig.shopify.storefrontToken
                    };
                }
                else if (provider === 'boost' && envConfig.boost.apiUrl) {
                    savedConfig.search.apiKeys.boost = {
                        apiUrl: envConfig.boost.apiUrl,
                        shop: envConfig.boost.shop
                    };
                }
            }
            return savedConfig;
        }
        // No saved config, create one from env and defaults
        return Object.assign(Object.assign({}, defaultDemoConfig), { search: Object.assign(Object.assign({}, defaultDemoConfig.search), { provider: provider, mockPlatform: (envConfig.mockPlatform || defaultDemoConfig.search.mockPlatform), apiKeys: {
                    algolia: {
                        appId: envConfig.algolia.appId,
                        apiKey: envConfig.algolia.apiKey,
                        indexName: envConfig.algolia.indexName
                    },
                    shopify: {
                        domain: envConfig.shopify.domain,
                        storefrontToken: envConfig.shopify.storefrontToken
                    },
                    unbxd: {
                        apiKey: envConfig.unbxd.apiKey,
                        siteKey: envConfig.unbxd.siteKey
                    },
                    boost: {
                        apiUrl: envConfig.boost.apiUrl,
                        shop: envConfig.boost.shop
                    }
                } }) });
    }
    static clearConfig() {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(API_KEYS_STORAGE_KEY);
    }
    static exportConfig(config) {
        // Export config without sensitive API keys
        const exportConfig = Object.assign(Object.assign({}, config), { search: Object.assign(Object.assign({}, config.search), { apiKeys: {} }) });
        return JSON.stringify(exportConfig, null, 2);
    }
    static importConfig(configString) {
        try {
            const imported = JSON.parse(configString);
            // Validate the imported config has required structure
            if (!imported.layout || !imported.pagination || !imported.features || !imported.search) {
                throw new Error('Invalid configuration structure');
            }
            // Ensure search has required fields
            if (!imported.search.provider) {
                throw new Error('Missing search provider');
            }
            // Initialize apiKeys if not present
            if (!imported.search.apiKeys) {
                imported.search.apiKeys = {};
            }
            // Sanitize the config to ensure valid values
            const sanitized = this.sanitizeConfig(imported);
            // Validate the sanitized config
            const validation = this.validateConfig(sanitized);
            if (!validation.valid) {
                console.warn('Configuration validation warnings:', validation.errors);
            }
            return sanitized;
        }
        catch (error) {
            console.error('Failed to import config:', error);
            return null;
        }
    }
    static downloadConfig(config) {
        const configString = this.exportConfig(config);
        const blob = new Blob([configString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `demo-config-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    static generateEnvFile(config) {
        const { provider, apiKeys, mockPlatform } = config.search;
        let envContent = '# Generated environment variables\n';
        envContent += '# Note: Environment variables take priority over Control Panel settings\n\n';
        // Add provider configuration
        envContent += `# Search Provider Configuration\n`;
        envContent += `PROVIDER=${provider}\n`;
        if (provider === 'mock') {
            envContent += `USE_MOCK_SERVER=true\n`;
            envContent += `MOCK_SERVER_URL=http://localhost:3001\n`;
            if (mockPlatform) {
                envContent += `# Mock platform is controlled via Control Panel only\n`;
            }
        }
        envContent += '\n';
        // Add provider-specific configuration
        switch (provider) {
            case 'algolia':
                if (apiKeys.algolia) {
                    envContent += `# Algolia Configuration\n`;
                    envContent += `ALGOLIA_APP_ID=${apiKeys.algolia.appId || ''}\n`;
                    envContent += `ALGOLIA_API_KEY=${apiKeys.algolia.apiKey || ''}\n`;
                    envContent += `ALGOLIA_INDEX=${apiKeys.algolia.indexName || ''}\n`;
                }
                break;
            case 'shopify':
                if (apiKeys.shopify) {
                    envContent += `# Shopify Configuration\n`;
                    envContent += `SHOPIFY_STORE_DOMAIN=${apiKeys.shopify.domain || ''}\n`;
                    envContent += `SHOPIFY_STOREFRONT_ACCESS_TOKEN=${apiKeys.shopify.storefrontToken || ''}\n`;
                }
                break;
            case 'unbxd':
                if (apiKeys.unbxd) {
                    envContent += `# Unbxd Configuration\n`;
                    envContent += `UNBXD_SITE_KEY=${apiKeys.unbxd.siteKey || ''}\n`;
                    envContent += `UNBXD_API_KEY=${apiKeys.unbxd.apiKey || ''}\n`;
                    envContent += `UNBXD_ENDPOINT=https://search.unbxd.io\n`;
                }
                break;
            case 'boost':
                if (apiKeys.boost) {
                    envContent += `# Boost Commerce Configuration\n`;
                    envContent += `BOOST_API_URL=${apiKeys.boost.apiUrl || ''}\n`;
                    envContent += `BOOST_SHOP=${apiKeys.boost.shop || ''}\n`;
                }
                break;
        }
        // Add common configuration
        envContent += `\n# Development Configuration\n`;
        envContent += `NODE_ENV=development\n`;
        return envContent;
    }
    static getConfigFromUrl() {
        try {
            const params = new URLSearchParams(window.location.search);
            const configParam = params.get('config');
            if (!configParam)
                return null;
            const decodedConfig = atob(configParam);
            return JSON.parse(decodedConfig);
        }
        catch (error) {
            console.error('Failed to parse config from URL:', error);
            return null;
        }
    }
    static getShareableUrl(config) {
        // Create shareable config without API keys
        const shareableConfig = Object.assign(Object.assign({}, config), { search: Object.assign(Object.assign({}, config.search), { apiKeys: {} }) });
        const encodedConfig = btoa(JSON.stringify(shareableConfig));
        const url = new URL(window.location.href);
        url.searchParams.set('config', encodedConfig);
        return url.toString();
    }
    static validateConfig(config) {
        const errors = [];
        // Validate layout
        if (!config.layout) {
            errors.push('Layout configuration is missing');
        }
        else {
            const validCardLayouts = ['gridTwo', 'gridFour', 'list'];
            if (!validCardLayouts.includes(config.layout.cardLayout)) {
                errors.push(`Invalid card layout: ${config.layout.cardLayout}`);
            }
            const validFilterLayouts = ['filterTop', 'filterSide', 'filterDrawer'];
            if (!validFilterLayouts.includes(config.layout.filterLayout)) {
                errors.push(`Invalid filter layout: ${config.layout.filterLayout}`);
            }
        }
        // Validate pagination
        if (!config.pagination) {
            errors.push('Pagination configuration is missing');
        }
        else {
            const validPaginationTypes = ['none', 'pagination', 'infiniteAuto', 'infiniteButton'];
            if (!validPaginationTypes.includes(config.pagination.type)) {
                errors.push(`Invalid pagination type: ${config.pagination.type}`);
            }
            const validItemsPerPage = [12, 24, 36, 48];
            if (!validItemsPerPage.includes(config.pagination.itemsPerPage)) {
                errors.push(`Invalid items per page: ${config.pagination.itemsPerPage}`);
            }
        }
        // Validate features
        if (!config.features) {
            errors.push('Features configuration is missing');
        }
        // Validate search
        if (!config.search) {
            errors.push('Search configuration is missing');
        }
        else {
            const validProviders = ['mock', 'algolia', 'shopify', 'unbxd', 'boost'];
            if (!validProviders.includes(config.search.provider)) {
                errors.push(`Invalid search provider: ${config.search.provider}`);
            }
            if (config.search.mockPlatform) {
                const validMockPlatforms = ['algolia', 'shopify', 'unbxd', 'boost'];
                if (!validMockPlatforms.includes(config.search.mockPlatform)) {
                    errors.push(`Invalid mock platform: ${config.search.mockPlatform}`);
                }
            }
            // Validate API keys based on provider
            if (config.search.provider !== 'mock' && config.search.apiKeys) {
                const provider = config.search.provider;
                const keys = config.search.apiKeys[provider];
                if (!keys) {
                    errors.push(`Missing API keys for provider: ${provider}`);
                }
                else {
                    switch (provider) {
                        case 'algolia':
                            const algoliaKeys = keys;
                            if (!algoliaKeys.appId)
                                errors.push('Algolia App ID is required');
                            if (!algoliaKeys.apiKey)
                                errors.push('Algolia API Key is required');
                            if (!algoliaKeys.indexName)
                                errors.push('Algolia Index Name is required');
                            break;
                        case 'shopify':
                            const shopifyKeys = keys;
                            if (!shopifyKeys.domain)
                                errors.push('Shopify domain is required');
                            if (!shopifyKeys.storefrontToken)
                                errors.push('Shopify storefront token is required');
                            break;
                        case 'unbxd':
                            const unbxdKeys = keys;
                            if (!unbxdKeys.apiKey)
                                errors.push('Unbxd API key is required');
                            if (!unbxdKeys.siteKey)
                                errors.push('Unbxd site key is required');
                            break;
                        case 'boost':
                            const boostKeys = keys;
                            if (!boostKeys.apiUrl)
                                errors.push('Boost API URL is required');
                            if (!boostKeys.shop)
                                errors.push('Boost shop is required');
                            break;
                    }
                }
            }
        }
        return {
            valid: errors.length === 0,
            errors
        };
    }
    static sanitizeConfig(config) {
        var _a, _b, _c, _d, _e, _f;
        // Ensure all required fields have valid values
        const sanitized = Object.assign({}, config);
        // Set defaults for missing or invalid values
        if (!((_a = sanitized.layout) === null || _a === void 0 ? void 0 : _a.cardLayout) || !['gridTwo', 'gridFour', 'list'].includes(sanitized.layout.cardLayout)) {
            sanitized.layout.cardLayout = 'gridFour';
        }
        if (!((_b = sanitized.layout) === null || _b === void 0 ? void 0 : _b.filterLayout) || !['filterTop', 'filterSide', 'filterDrawer'].includes(sanitized.layout.filterLayout)) {
            sanitized.layout.filterLayout = 'filterSide';
        }
        if (!((_c = sanitized.pagination) === null || _c === void 0 ? void 0 : _c.type) || !['none', 'pagination', 'infiniteAuto', 'infiniteButton'].includes(sanitized.pagination.type)) {
            sanitized.pagination.type = 'pagination';
        }
        if (!((_d = sanitized.pagination) === null || _d === void 0 ? void 0 : _d.itemsPerPage) || ![12, 24, 36, 48].includes(sanitized.pagination.itemsPerPage)) {
            sanitized.pagination.itemsPerPage = 24;
        }
        if (!((_e = sanitized.search) === null || _e === void 0 ? void 0 : _e.provider) || !['mock', 'algolia', 'shopify', 'unbxd', 'boost'].includes(sanitized.search.provider)) {
            sanitized.search.provider = 'mock';
        }
        if (!((_f = sanitized.search) === null || _f === void 0 ? void 0 : _f.apiKeys)) {
            sanitized.search.apiKeys = {};
        }
        return sanitized;
    }
}
